/**
 * Local coin wallet + equipped ball skin (handegg-roster.md "Pricing", "Ball skins rule"): the web UI's binding of
 * `@hf/shared`'s client-local `createLocalWallet` to `localStorage` (through the shared `Platform` seam), plus one
 * extra bit of local state `@hf/shared`'s wallet doesn't model itself — which OWNED ball skin is equipped (the
 * wallet only tracks ownership/balance, not a current selection).
 *
 * Both the Store screen and CharacterSelect read/write through here. The match renderer
 * (`apps/client/src/render/ball-skin-settings.ts`) mirrors the equipped-skin storage key directly instead of
 * importing this module (render may not value-import `ui`, see its README "Boundaries") — keep the two key names
 * in sync.
 */
import { effect, signal } from '@preact/signals';
import { soloSide } from '@hf/client-core/ui-model/screens';
import { ui } from '@hf/client-core/ui-model/store';
import { track } from '@hf/client-core/analytics';
import { getPlatform } from '@hf/client-core/platform';
import { createSyncedWallet, type SyncedWallet } from '@hf/client-core/account/synced-wallet';
import { createLocalWallet, isBallSkinId, isCharacterId, MATCH_REWARDS, type BallSkinId } from '@hf/shared';
import { account, lobbySocketAuthed } from '../net/account';
import { PORTAL } from '../portal';
import { googleWebSignOut } from '../net/google-web-signin';

/** Keep in sync with `apps/client/src/render/ball-skin-settings.ts` `KEY`. */
const EQUIPPED_BALL_SKIN_KEY = 'hf.equippedBallSkin';

const storage = getPlatform().storage();

/** Server-backed once this browser's guest account syncs (accounts slice 1); the plain local wallet until then. */
export const wallet: SyncedWallet = createSyncedWallet({
  local: createLocalWallet(storage),
  storage,
  account,
  onChange: () => bumpBalance(),
});

/** Reactive coin balance (re-renders the coin pill wherever it's shown): every mutation below refreshes it. */
export const walletBalance = signal(wallet.getBalance());
function bumpBalance(): void {
  walletBalance.value = wallet.getBalance();
}
// Portal build: no coins: every head and ball skin is playable, nothing syncs.
if (PORTAL) wallet.owns = () => true;
else void wallet.sync();

/** Spend coins and grant `sku` (a character or ball-skin id); true (no spend) if already owned or free, false if
 *  unaffordable. Refreshes `walletBalance` on success. */
export const unlockSku = (sku: string, priceCoins: number): boolean => {
  const already = wallet.owns(sku);
  const ok = wallet.unlock(sku, priceCoins);
  if (ok) {
    bumpBalance();
    if (!already) {
      const balance_after = wallet.getBalance();
      if (isCharacterId(sku))
        track('character_unlocked', { head_id: sku, price_coins: priceCoins, balance_after });
      if (isCharacterId(sku) || isBallSkinId(sku))
        track('item_purchased', {
          item_id: sku,
          item_type: isCharacterId(sku) ? 'head' : 'ball',
          price_coins: priceCoins,
          platform: 'web',
        });
      else if (isBallSkinId(sku))
        track('ball_skin_unlocked', { skin_id: sku, price_coins: priceCoins, balance_after });
    }
  }
  return ok;
};

/** Credit coins (match reward, grant) and refresh `walletBalance`. */
export const creditWallet = (amount: number, reason: string): number => {
  const balance = wallet.credit(amount, reason);
  bumpBalance();
  return balance;
};

// ── Match payouts (mirrors apps/mobile store/wallet.ts; handegg-roster.md "Coins earned per match") ─────────────
// Paid once per finished match when `ui.results` is published: vs-CPU via `rewardBotMatch` (server: POST
// /rewards/bot, daily cap + 45 s minimum), online quick/ranked via `rewardOnlineMatch` (the server pays from the
// lobby's verdict; we show the estimate, then the confirmed amount). Local 2P / private / tutorial / practice: 0.
/** coin_earned (legacy, with balance) + coins_earned {amount, source: 'match'} (store funnel). */
const earned = (source: 'match_bot' | 'match_online', amount: number): void => {
  track('coin_earned', { source, amount, balance_after: wallet.getBalance() });
  track('coins_earned', { amount, source: 'match' });
};

export const lastPayout = signal<number>(0);
export type PayoutNote = 'short' | 'cap' | null;
export const lastPayoutNote = signal<PayoutNote>(null);

let matchStartedAt = 0;
const paid = new WeakSet<object>();
effect(() => {
  if (ui.screen.value === 'match' && matchStartedAt === 0) matchStartedAt = Date.now();
});
effect(() => {
  const r = ui.results.value;
  if (PORTAL || !r || paid.has(r)) return;
  paid.add(r);
  const setup = ui.matchSetup.peek();
  const startedAt = matchStartedAt;
  matchStartedAt = 0;
  lastPayout.value = 0;
  lastPayoutNote.value = null;
  if (!setup) return;
  let amount = 0;
  if (setup.mode === 'cpu') {
    const you = soloSide(setup);
    if (you === -1) return;
    const durationSec = startedAt > 0 ? (Date.now() - startedAt) / 1000 : 0;
    amount = wallet.rewardBotMatch(r.winner === you, durationSec);
    if (amount === 0) lastPayoutNote.value = durationSec < MATCH_REWARDS.bot.minDurationSec ? 'short' : 'cap';
    else earned('match_bot', amount);
  } else if ((setup.mode === 'online_quick' || setup.mode === 'online_ranked') && setup.online) {
    if (r.status === 'disputed') return;
    const won = r.winner === setup.online.you;
    amount = wallet.rewardOnlineMatch(won, {
      forfeit: won && r.status === 'forfeit',
      authed: lobbySocketAuthed(),
      durationSec: startedAt > 0 ? (Date.now() - startedAt) / 1000 : undefined,
    });
    if (wallet.mode() === 'server') {
      void wallet.confirmOnlineReward(setup.online.matchId).then((got) => {
        if (got === null || ui.results.peek() !== r) return;
        lastPayout.value = got;
        bumpBalance();
        if (got > 0) earned('match_online', got);
      });
    } else if (amount > 0) {
      earned('match_online', amount);
    }
  }
  lastPayout.value = amount;
  bumpBalance();
});

// ── Equipped ball skin (cosmetic selection among OWNED skins; not part of @hf/shared's wallet) ──────────────────

const readEquippedBallSkin = (): BallSkinId => {
  const raw = storage?.getItem(EQUIPPED_BALL_SKIN_KEY);
  return raw && isBallSkinId(raw) ? raw : 'classic';
};

export const equippedBallSkin = signal<BallSkinId>(readEquippedBallSkin());

/** Equip an OWNED ball skin; no-op (returns false) for a skin not yet unlocked. */
export const setEquippedBallSkin = (id: BallSkinId): boolean => {
  if (!wallet.owns(id)) return false;
  if (equippedBallSkin.peek() === id) return true;
  equippedBallSkin.value = id;
  storage?.setItem(EQUIPPED_BALL_SKIN_KEY, id);
  track('item_equipped', { item_id: id, item_type: 'ball' });
  return true;
};

/** Web account state for Settings → ACCOUNT: null = unknown / offline. */
export interface WebAccountInfo {
  guest: boolean;
  name: string | null;
  providers: string[];
}
export const webAccount = signal<WebAccountInfo | null>(null);
/** GET /me has answered (or failed) at least once: the sign-in gate waits for it instead of flashing. */
export const webAccountChecked = signal(false);
/** Sign-in gate → PLAY OFFLINE for this session: vs CPU / local / practice without an account. */
export const webPlayingOffline = signal(false);
/** Signed in = at least one linked identity (Google on the web; Apple / Google / email from the apps). */
export const isWebSignedIn = (): boolean => (webAccount.peek()?.providers.length ?? 0) > 0;
/** Online, the store and leaderboards: true when signed in; else re-show the sign-in gate and return false. */
export const requireWebSignIn = (): boolean => {
  if (!account || isWebSignedIn()) return true;
  webPlayingOffline.value = false;
  return false;
};

export const refreshWebAccount = async (): Promise<void> => {
  if (!account) return;
  const r = await account.call<{
    user?: { guest?: boolean; display_name?: string | null };
    identities?: { provider?: string }[];
  }>('GET', '/me');
  webAccountChecked.value = true;
  if (r.status !== 200 || !r.body?.user) return;
  webAccount.value = {
    guest: !!r.body.user.guest,
    name: r.body.user.display_name ?? null,
    providers: (r.body.identities ?? []).map((i) => String(i.provider ?? '')).filter(Boolean),
  };
};

/** Settings → ACCOUNT → Sign in with Google: POST /auth/link/google, then re-sync the wallet. */
export const linkGoogleWeb = async (
  idToken: string,
): Promise<'ok' | 'offline' | 'error' | 'merge_blocked' | 'unavailable'> => {
  if (!account) return 'unavailable';
  const r = await account.linkGoogle({ idToken });
  if (r.status === 0) return 'offline';
  if (r.status === 409 && (r.body as { error?: string } | null)?.error === 'merge_blocked')
    return 'merge_blocked';
  if (r.status !== 200) return 'error';
  await wallet.sync();
  bumpBalance();
  track('account_linked', { provider: 'google', from_state: 'guest' });
  await refreshWebAccount();
  return 'ok';
};

/** Settings → ACCOUNT → Sign in with Apple (web JS): POST /auth/link/apple, then re-sync the wallet. */
export const linkAppleWeb = async (cred: {
  idToken: string;
  nonce: string;
  fullName: string | null;
}): Promise<'ok' | 'offline' | 'error' | 'merge_blocked' | 'unavailable'> => {
  if (!account) return 'unavailable';
  const r = await account.linkApple(cred);
  if (r.status === 0) return 'offline';
  if (r.status === 409 && (r.body as { error?: string } | null)?.error === 'merge_blocked')
    return 'merge_blocked';
  if (r.status !== 200) return 'error';
  await wallet.sync();
  bumpBalance();
  track('account_linked', { provider: 'apple', from_state: 'guest' });
  await refreshWebAccount();
  return 'ok';
};

/**
 * Settings → ACCOUNT → Delete account: `DELETE /me` (account + linked identities + wallet + ranked Elo, PR #31),
 * then the same local reset as sign-out (a fresh guest takes its place).
 */
export const deleteAccountWeb = async (): Promise<'ok' | 'offline' | 'error' | 'unavailable'> => {
  if (!account) return 'unavailable';
  const wasGoogle = webAccount.peek()?.providers.includes('google') ?? false;
  const r = await account.deleteAccount();
  if (r !== 'ok') return r;
  if (wasGoogle) googleWebSignOut();
  await wallet.resetAfterDelete();
  setEquippedBallSkin('classic');
  bumpBalance();
  webAccount.value = { guest: true, name: null, providers: [] };
  webPlayingOffline.value = false;
  await refreshWebAccount();
  return 'ok';
};

/**
 * Settings → ACCOUNT → Sign out: `POST /auth/logout` (best effort), clear the stored session (a fresh guest takes
 * its place), stop Google auto-select when Google was the provider, and drop the account's synced wallet.
 */
export const signOutWeb = async (): Promise<'ok' | 'offline' | 'unavailable'> => {
  if (!account) return 'unavailable';
  const wasGoogle = webAccount.peek()?.providers.includes('google') ?? false;
  const r = await account.logout();
  if (wasGoogle) googleWebSignOut();
  await wallet.resetAfterDelete();
  setEquippedBallSkin('classic');
  bumpBalance();
  webAccount.value = { guest: true, name: null, providers: [] };
  webPlayingOffline.value = false; // back to the sign-in gate (PLAY OFFLINE is still there)
  await refreshWebAccount();
  return r;
};
